import React, { forwardRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { NotificationStyles } from './Notification.styles.js';
import { Button } from '../Button/Button.js';
import { IconNavigationClose, IconCommerceSparkle, IconStatusInfo, IconStatusSuccess, IconStatusWarning, IconStatusError } from '@s-group/design-system-icons';

const defaultProps = {
    alignment: 'left',
    close: true,
    orientation: 'vertical',
    rounding: 'sharp',
    sizing: 'medium',
    variant: 'tonal',
};
const defaultStatusIcons = {
    info: IconStatusInfo,
    success: IconStatusSuccess,
    warning: IconStatusWarning,
    error: IconStatusError,
};
const SCREEN_READER_NOTIFICATION_DELAY = 100;
const isIconComponent = (icon) => icon !== undefined && typeof icon !== 'boolean';
const resolveDefaultIcon = ({ icon, status, }) => isIconComponent(icon)
    ? // Use icon component, if it is provided.
        icon
    : status && icon !== false
        ? // The status notifications have status specific icons by default.
            // The default icon can be disabled with `icon={false}`.
            [defaultStatusIcons[status], {}]
        : icon === true
            ? // The general notification has an optional default icon that can be
                // enabled with `icon={true}`.
                [IconCommerceSparkle, {}]
            : // If the icon prop is not set, the general notification has no icon.
                undefined;
const IconContainer = (props) => {
    const icon = resolveDefaultIcon(props);
    if (icon) {
        const [IconComponent, iconProps] = Array.isArray(icon)
            ? icon
            : 'props' in icon
                ? [icon.type, icon.props]
                : [icon, {}];
        return (React.createElement("div", { className: "icon-container" },
            React.createElement(IconComponent, { className: "leading-icon", ...iconProps })));
    }
    return null;
};
const ContentContainer = ({ header, children, trailer, ...rest }) => (React.createElement("div", { className: "content-container", ...rest },
    React.createElement("div", null,
        header && React.createElement("div", { className: "header" }, header),
        children),
    trailer && React.createElement("div", { className: "trailer" }, trailer)));
const CloseContainer = ({ close, closeProps }) => close ? (React.createElement("div", { className: "close-container" },
    React.createElement(Button
    // TODO: Take a proper localization solution in use.
    // Currently a Finnish aria-label is hard-coded here as
    // an `aria-label` is mandatory in an icon-only Button and
    // Finnish is the main language in S group services.
    // It is possible to override this using ´closeProps`.
    , { "aria-label": "Sulje", color: "grey", icon: React.createElement(IconNavigationClose, null), sizing: "small", variant: "plain", ...closeProps }))) : null;
const _Notification = forwardRef(({ alignment, // Used only in styling, prevent passing it to DOM
'aria-live': ariaLive, children, close, closeProps, contentProps, header, icon, orientation, // Used only in styling, prevent passing it to DOM
rounding, // Used only in styling, prevent passing it to DOM
sizing, // Used only in styling, prevent passing it to DOM
status, trailer, variant, // Used only in styling, prevent passing it to DOM
...rest }, ref) => {
    const staticNotification = ariaLive === 'off';
    const [hidden, setHidden] = useState(!staticNotification);
    useEffect(() => {
        if (!staticNotification) {
            setTimeout(() => {
                setHidden(false);
            }, SCREEN_READER_NOTIFICATION_DELAY);
        }
    }, []);
    return (React.createElement("div", { "aria-atomic": "true", "aria-live": ariaLive, ...rest, ref: ref },
        React.createElement(IconContainer, { icon: icon, status: status }),
        React.createElement(ContentContainer, { "aria-hidden": hidden, header: header, trailer: trailer, ...contentProps }, children),
        React.createElement(CloseContainer, { close: close, closeProps: closeProps })));
});
_Notification.displayName = '_Notification';
const Notification = styled(_Notification).attrs(({ alignment, close, orientation, role, rounding, sizing, status, variant, }) => {
    return {
        alignment: alignment !== null && alignment !== void 0 ? alignment : defaultProps.alignment,
        close: variant === 'plain' ? false : close !== null && close !== void 0 ? close : defaultProps.close,
        orientation: orientation !== null && orientation !== void 0 ? orientation : defaultProps.orientation,
        role: role !== null && role !== void 0 ? role : (status === 'error' ? 'alert' : 'status'),
        rounding: variant === 'plain'
            ? 'sharp'
            : rounding !== null && rounding !== void 0 ? rounding : defaultProps.rounding,
        sizing: sizing !== null && sizing !== void 0 ? sizing : defaultProps.sizing,
        variant: variant !== null && variant !== void 0 ? variant : defaultProps.variant,
    };
}) `
    ${NotificationStyles}
`;

export { Notification, defaultProps, defaultStatusIcons, resolveDefaultIcon };
//# sourceMappingURL=Notification.js.map
